import React, { FC, Fragment, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Organization, OrgLookup } from 'models/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { syncHitHorizonsOrganizationTemplate } from 'apollo/templates/organization';
import { useLazyQuery, useMutation } from '@apollo/client';
import { hithorizonsLookupTemplate } from 'apollo/templates/organization/query.graphql';
import FormattedAddress from 'components/shared/address/formatted-address.component';
import { LoadingButton } from '@mui/lab';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Address } from 'models/contact';

type SyncHitHoriozonsDialogProps = {
    organization?: Organization;
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onOrganizationModified: () => void;
};
const SyncHitHoriozonsDialog: FC<SyncHitHoriozonsDialogProps> = ({
    organization,
    dialogOpen,
    onCloseDialog,
    onOrganizationModified,
}: SyncHitHoriozonsDialogProps) => {
    const { t: translate } = useTranslation(['organization']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [hitHorizonsOrg, setHitHorizonsOrg] = useState<OrgLookup>();
    const [currentOrganization, setCurrentOrganization] = useState<Organization | undefined>(organization);
    const [isLoading, setIsLoading] = useState(true);
    const [syncHHId, setSyncHHId] = useState(false);
    const [syncOfficialName, setSyncOfficialName] = useState(false);
    const [syncVatNumber, setSyncVatNumber] = useState(false);
    const [syncSite, setSyncSite] = useState(false);
    const [syncAddress, setSyncAddress] = useState(false);
    const [hhIdInSync, setHHIdInSync] = useState(false);
    const [officialNameInSync, setOfficialNameInSync] = useState(false);
    const [vatNumberInSync, setVatNumberInSync] = useState(false);
    const [siteInSync, setSiteInSync] = useState(false);
    const [addressInSync, setAddressInSync] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const [hithorizonsLookup] = useLazyQuery(hithorizonsLookupTemplate, {
        onError: (error) => {
            setErrorMessage(error.message);
            setIsLoading(false);
        },
        onCompleted: async ({ hithorizonsLookup }) => {
            if (dialogOpen) {
                setHitHorizonsOrg(hithorizonsLookup);
                setOfficialNameInSync(hithorizonsLookup?.name === currentOrganization?.officialName);
                setVatNumberInSync(hithorizonsLookup?.vatNumber === currentOrganization?.invoicing?.vatNo);
                setSiteInSync(
                    hithorizonsLookup?.site?.trim() === currentOrganization?.contact?.communication?.site?.trim(),
                );
                setAddressInSync(isSameAddress(hithorizonsLookup?.address, currentOrganization?.contact?.address));
                setHHIdInSync(hithorizonsLookup?.hitHorizonsId === currentOrganization?.hitHorizonsId);
                setSyncHHId(!currentOrganization?.hitHorizonsId);
                setIsLoading(false);
            }
        },
    });

    const [syncHitHorizonsOrganization] = useMutation(syncHitHorizonsOrganizationTemplate, {
        onCompleted: ({}) => {
            resetDialogState();
            onOrganizationModified();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (organization?._id && dialogOpen) {
            setCurrentOrganization(organization);
            hithorizonsLookup({
                variables: {
                    organizationId: organization?._id,
                },
            });
        }
    }, [organization]);

    const resetDialogState = () => {
        setCurrentOrganization(undefined);
        setErrorMessage(undefined);
        setHitHorizonsOrg(undefined);
        setSyncOfficialName(false);
        setSyncVatNumber(false);
        setSyncSite(false);
        setSyncAddress(false);
        setIsLoading(true);
    };

    const handleCloseDialog = () => {
        resetDialogState();
        onCloseDialog();
    };

    const isSameAddress = (address1: Address | undefined, address2: Address | undefined) => {
        if (address1 && address2) {
            return (
                address1.street === address2.street &&
                address1.city === address2.city &&
                address1.postalCode === address2.postalCode &&
                address1.country === address2.country
            );
        }
        return false;
    };

    const syncOrganization = () => {
        syncHitHorizonsOrganization({
            variables: {
                organizationId: currentOrganization?._id,
                syncData: {
                    hitHorizonsId: syncHHId ? hitHorizonsOrg?.hitHorizonsId : undefined,
                    officialName: syncOfficialName ? hitHorizonsOrg?.name : undefined,
                    vatNumber: syncVatNumber ? hitHorizonsOrg?.vatNumber : undefined,
                    address: syncAddress ? hitHorizonsOrg?.address : undefined,
                    site: syncSite ? hitHorizonsOrg?.site : undefined,
                },
            },
        });
    };

    return (
        <Dialog fullWidth maxWidth="lg" open={dialogOpen} onClose={handleCloseDialog}>
            <DialogTitle>{`${currentOrganization?.name} - ${translate('organization:SYNC_HIT_HORIZONS')}`}</DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Box sx={{ minHeight: '200px' }} display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={80} />
                    </Box>
                ) : (
                    <Fragment>
                        {errorMessage ? (
                            <Alert severity="error">{errorMessage}</Alert>
                        ) : (
                            <Fragment>
                                <TableContainer>
                                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width="50"></TableCell>
                                                <TableCell width="150">Data</TableCell>
                                                <TableCell>HitHorizons</TableCell>
                                                <TableCell>Denthub</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell align="center">
                                                    {hhIdInSync ? (
                                                        <CheckCircleIcon color="success" />
                                                    ) : (
                                                        <Checkbox checked={syncHHId} />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <strong>hitHorizonsId</strong>
                                                </TableCell>
                                                <TableCell>{hitHorizonsOrg?.hitHorizonsId}</TableCell>
                                                <TableCell>{currentOrganization?.hitHorizonsId}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell align="center">
                                                    <CheckCircleIcon color="success" />
                                                </TableCell>
                                                <TableCell>
                                                    <strong>organizationNumber</strong>
                                                </TableCell>
                                                <TableCell>{hitHorizonsOrg?.organizationNumber}</TableCell>
                                                <TableCell>{currentOrganization?.organizationNumber}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell align="center">
                                                    {officialNameInSync ? (
                                                        <CheckCircleIcon color="success" />
                                                    ) : (
                                                        <Checkbox
                                                            checked={syncOfficialName}
                                                            onChange={(e) => setSyncOfficialName(e.target.checked)}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <strong>officialName</strong>
                                                </TableCell>
                                                <TableCell>{hitHorizonsOrg?.name}</TableCell>
                                                <TableCell>{currentOrganization?.officialName}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell align="center">
                                                    {vatNumberInSync ? (
                                                        <CheckCircleIcon color="success" />
                                                    ) : (
                                                        <Checkbox
                                                            checked={syncVatNumber}
                                                            onChange={(e) => setSyncVatNumber(e.target.checked)}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <strong>vatNumber</strong>
                                                </TableCell>
                                                <TableCell>{hitHorizonsOrg?.vatNumber}</TableCell>
                                                <TableCell>{currentOrganization?.invoicing?.vatNo}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell align="center">
                                                    {siteInSync ? (
                                                        <CheckCircleIcon color="success" />
                                                    ) : (
                                                        <Checkbox
                                                            checked={syncSite}
                                                            onChange={(e) => setSyncSite(e.target.checked)}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <strong>site</strong>
                                                </TableCell>
                                                <TableCell>{hitHorizonsOrg?.site}</TableCell>
                                                <TableCell>
                                                    {currentOrganization?.contact?.communication?.site}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell align="center">
                                                    {addressInSync ? (
                                                        <CheckCircleIcon color="success" />
                                                    ) : (
                                                        <Checkbox
                                                            checked={syncAddress}
                                                            onChange={(e) => setSyncAddress(e.target.checked)}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <strong>address</strong>
                                                </TableCell>
                                                <TableCell>
                                                    <FormattedAddress address={hitHorizonsOrg?.address} oneLineFormat />
                                                </TableCell>
                                                <TableCell>
                                                    <FormattedAddress
                                                        address={currentOrganization?.contact?.address}
                                                        oneLineFormat
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {syncAddress ? (
                                    <Box mt={1}>
                                        <Alert severity="warning">
                                            {translate('organization:SYNC_HIT_HORIZONS_ADDRESS_INFO')}
                                        </Alert>
                                    </Box>
                                ) : undefined}
                            </Fragment>
                        )}
                    </Fragment>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseDialog}>{translate('common:CANCEL')}</Button>
                <LoadingButton
                    variant="contained"
                    disabled={!syncHHId && !syncOfficialName && !syncVatNumber && !syncSite && !syncAddress}
                    onClick={() => {
                        syncOrganization();
                    }}
                >
                    {translate('common:SAVE')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default SyncHitHoriozonsDialog;
